// pages/profile/Profile.jsx
import React, { useState, useEffect } from 'react';
import { authActions } from '../../core/action/authActions';
import { companyActions } from '../../core/action/companyActions';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { 
    User, Edit2, Save, X, Lock, Eye, EyeOff 
} from 'feather-icons-react/build/IconComponents';
//import '../../style/css/profile.css';

const MySwal = withReactContent(Swal);

const Profile = () => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [formData, setFormData] = useState({});
    const [companySettings, setCompanySettings] = useState({});
    const [profileImage, setProfileImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    // Password change states
    const [passwordData, setPasswordData] = useState({
        current_password: '',
        new_password: '',
        confirm_password: ''
    });
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [changingPassword, setChangingPassword] = useState(false);

    useEffect(() => {
        loadUserProfile();
        loadCompanySettings();
    }, []);

    const loadUserProfile = async () => {
        setLoading(true);
        try {
            const result = await authActions.getProfile();
            if (result.success && result.data) {
                setUser(result.data);
                setFormData(result.data);
                if (result.data.profile_image) {
                    setImagePreview(result.data.profile_image);
                }
            } else {
                MySwal.fire('Error', result.message || 'Failed to load profile', 'error');
            }
        } catch (error) {
            console.error('Error loading profile:', error);
            MySwal.fire('Error', 'Failed to load profile', 'error');
        } finally {
            setLoading(false);
        }
    };

    const loadCompanySettings = async () => {
        try {
            const result = await companyActions.getSettings();
            if (result.success) {
                setCompanySettings(result.data);
                console.log('Company Settings:',companySettings);
            }
        } catch (error) {
            console.error('Error loading company settings:', error);
        }
    };

    // Handle profile input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Handle password input change
    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setProfileImage(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleEditToggle = () => {
        setEditing(!editing);
        if (!editing) {
            setFormData(user);
            setImagePreview(user?.profile_image);
        }
    };

    const handleSave = async () => {
        try {
            const submitData = new FormData();
            
            // Append all form fields
            Object.keys(formData).forEach(key => {
                if (formData[key] !== null && formData[key] !== undefined) {
                    submitData.append(key, formData[key]);
                }
            });
            
            // Append profile image if changed
            if (profileImage) {
                submitData.append('profile_image', profileImage);
            }

            const result = await authActions.updateProfile(submitData);
            
            if (result.success) {
                MySwal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Profile updated successfully',
                    timer: 2000,
                    showConfirmButton: false
                });
                setUser(result.data);
                setEditing(false);
            } else {
                MySwal.fire('Error', result.message || 'Failed to update profile', 'error');
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            MySwal.fire('Error', 'Failed to update profile', 'error');
        }
    };

    // Validate password form
    const validatePasswordForm = () => {
        if (!passwordData.current_password) {
            MySwal.fire('Error', 'Current password is required', 'error');
            return false;
        }
        if (!passwordData.new_password) {
            MySwal.fire('Error', 'New password is required', 'error');
            return false;
        }
        if (passwordData.new_password.length < 6) {
            MySwal.fire('Error', 'New password must be at least 6 characters', 'error');
            return false;
        }
        if (passwordData.new_password !== passwordData.confirm_password) {
            MySwal.fire('Error', 'New passwords do not match', 'error');
            return false;
        }
        if (passwordData.new_password === passwordData.current_password) {
            MySwal.fire('Error', 'New password must be different from current password', 'error');
            return false;
        }
        return true;
    };

    // Handle password change
    const handlePasswordChangeSubmit = async () => {
        if (!validatePasswordForm()) return;

        setChangingPassword(true);
        try {
            debugger;
            const result = await authActions.changePassword(
                passwordData.current_password,
                passwordData.new_password
            );

            if (result.success) {
                MySwal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Password changed successfully',
                    timer: 2000,
                    showConfirmButton: false
                });
                // Clear password fields
                setPasswordData({
                    current_password: '',
                    new_password: '',
                    confirm_password: ''
                });
            } else {
                MySwal.fire('Error', result.message || 'Failed to change password', 'error');
            }
        } catch (error) {
            console.error('Error changing password:', error);
            MySwal.fire('Error', 'Failed to change password', 'error');
        } finally {
            setChangingPassword(false);
        }
    };

    // Reset password form
    const resetPasswordForm = () => {
        setPasswordData({
            current_password: '',
            new_password: '',
            confirm_password: ''
        });
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="page-wrapper">
                <div className="content container-fluid">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-wrapper">
            <div className="content container-fluid">
                {/* Page Header */}
                <div className="page-header">
                    <div className="row align-items-center">
                        <div className="col">
                            <h3 className="page-title">My Profile</h3>
                            <ul className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <a href="/dashboard">Dashboard</a>
                                </li>
                                <li className="breadcrumb-item active">Profile</li>
                            </ul>
                        </div>
                        <div className="col-auto text-end">
                            {!editing ? (
                                <button 
                                    className="btn btn-primary" 
                                    onClick={handleEditToggle}
                                >
                                    <Edit2 size={16} className="me-1" />
                                    Edit Profile
                                </button>
                            ) : (
                                <div>
                                    <button 
                                        className="btn btn-success me-2" 
                                        onClick={handleSave}
                                    >
                                        <Save size={16} className="me-1" />
                                        Save Changes
                                    </button>
                                    <button 
                                        className="btn btn-secondary" 
                                        onClick={handleEditToggle}
                                    >
                                        <X size={16} className="me-1" />
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Profile Content */}
                <div className="row">
                    <div className="col-md-12">
                        {/* Profile Information Card */}
                        <div className="card">
                            <div className="card-body">
                                <div className="row">
                                    {/* Profile Image Column */}
                                    <div className="col-md-4 text-center mb-4">
                                        <div className="profile-img-container">
                                            {editing ? (
                                                <div>
                                                    <div className="profile-img mb-3">
                                                        {imagePreview ? (
                                                            <img 
                                                                src={imagePreview} 
                                                                alt="Profile" 
                                                                className="rounded-circle img-fluid"
                                                                style={{ 
                                                                    width: '150px', 
                                                                    height: '150px', 
                                                                    objectFit: 'cover',
                                                                    border: '3px solid #fff',
                                                                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                                                                }}
                                                            />
                                                        ) : (
                                                            <div 
                                                                className="rounded-circle bg-light d-flex align-items-center justify-content-center mx-auto"
                                                                style={{ 
                                                                    width: '150px', 
                                                                    height: '150px',
                                                                    border: '3px solid #fff',
                                                                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                                                                }}
                                                            >
                                                                <User size={60} className="text-muted" />
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="mt-2">
                                                        <label 
                                                            htmlFor="profile_image" 
                                                            className="btn btn-outline-primary btn-sm"
                                                        >
                                                            Change Photo
                                                        </label>
                                                        <input
                                                            type="file"
                                                            id="profile_image"
                                                            className="d-none"
                                                            accept="image/*"
                                                            onChange={handleFileChange}
                                                        />
                                                    </div>
                                                </div>
                                            ) : (
                                                <div>
                                                    <div className="profile-img mb-3">
                                                        {user?.profile_image ? (
                                                            <img 
                                                                src={user.profile_image} 
                                                                alt={user.name} 
                                                                className="rounded-circle img-fluid"
                                                                style={{ 
                                                                    width: '150px', 
                                                                    height: '150px', 
                                                                    objectFit: 'cover',
                                                                    border: '3px solid #fff',
                                                                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                                                                }}
                                                            />
                                                        ) : (
                                                            <div 
                                                                className="rounded-circle bg-light d-flex align-items-center justify-content-center mx-auto"
                                                                style={{ 
                                                                    width: '150px', 
                                                                    height: '150px',
                                                                    border: '3px solid #fff',
                                                                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                                                                }}
                                                            >
                                                                <User size={60} className="text-muted" />
                                                            </div>
                                                        )}
                                                    </div>
                                                    <h4 className="text-dark mb-1">{user?.name || 'User'}</h4>
                                                    <p className="text-muted mb-0">{user?.email || 'N/A'}</p>
                                                    <p className="text-muted">
                                                        <small>Employee ID: {user?.employee_id || 'N/A'}</small>
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Profile Details Column */}
                                    <div className="col-md-8">
                                        <div className="profile-info">
                                            <h5 className="section-title text-dark mb-3">Profile Information</h5>
                                            
                                            <div className="row">
                                                {/* Full Name */}
                                                <div className="col-md-6 mb-3">
                                                    <div className="info-item">
                                                        <label className="text-muted mb-1">Full Name</label>
                                                        {editing ? (
                                                            <input
                                                                type="text"
                                                                name="name"
                                                                className="form-control"
                                                                value={formData.name || ''}
                                                                onChange={handleInputChange}
                                                            />
                                                        ) : (
                                                            <p className="text-dark fw-bold mb-0">{user?.name || 'N/A'}</p>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Email */}
                                                <div className="col-md-6 mb-3">
                                                    <div className="info-item">
                                                        <label className="text-muted mb-1">Email</label>
                                                        {editing ? (
                                                            <input
                                                                type="email"
                                                                name="email"
                                                                className="form-control"
                                                                value={formData.email || ''}
                                                                onChange={handleInputChange}
                                                                disabled
                                                            />
                                                        ) : (
                                                            <p className="text-dark mb-0">{user?.email || 'N/A'}</p>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Phone */}
                                                <div className="col-md-6 mb-3">
                                                    <div className="info-item">
                                                        <label className="text-muted mb-1">Phone</label>
                                                        {editing ? (
                                                            <input
                                                                type="tel"
                                                                name="phone"
                                                                className="form-control"
                                                                value={formData.phone || ''}
                                                                onChange={handleInputChange}
                                                                placeholder="Enter phone number"
                                                            />
                                                        ) : (
                                                            <p className="text-dark mb-0">{user?.phone || 'N/A'}</p>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Department */}
                                                <div className="col-md-6 mb-3">
                                                    <div className="info-item">
                                                        <label className="text-muted mb-1">Department</label>
                                                        {editing ? (
                                                            <input
                                                                type="text"
                                                                name="department"
                                                                className="form-control"
                                                                value={formData.department || ''}
                                                                onChange={handleInputChange}
                                                                placeholder="Enter department"
                                                            />
                                                        ) : (
                                                            <p className="text-dark mb-0">{user?.department || 'N/A'}</p>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Designation */}
                                                <div className="col-md-6 mb-3">
                                                    <div className="info-item">
                                                        <label className="text-muted mb-1">Designation</label>
                                                        {editing ? (
                                                            <input
                                                                type="text"
                                                                name="designation"
                                                                className="form-control"
                                                                value={formData.designation || ''}
                                                                onChange={handleInputChange}
                                                                placeholder="Enter designation"
                                                            />
                                                        ) : (
                                                            <p className="text-dark mb-0">{user?.designation || 'N/A'}</p>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Gender */}
                                                <div className="col-md-6 mb-3">
                                                    <div className="info-item">
                                                        <label className="text-muted mb-1">Gender</label>
                                                        {editing ? (
                                                            <select
                                                                name="gender"
                                                                className="form-select"
                                                                value={formData.gender || ''}
                                                                onChange={handleInputChange}
                                                            >
                                                                <option value="">Select Gender</option>
                                                                <option value="male">Male</option>
                                                                <option value="female">Female</option>
                                                                <option value="other">Other</option>
                                                            </select>
                                                        ) : (
                                                            <p className="text-dark mb-0">
                                                                {user?.gender ? user.gender.charAt(0).toUpperCase() + user.gender.slice(1) : 'N/A'}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Joining Date */}
                                                <div className="col-md-6 mb-3">
                                                    <div className="info-item">
                                                        <label className="text-muted mb-1">Joining Date</label>
                                                        {editing ? (
                                                            <input
                                                                type="date"
                                                                name="joining_date"
                                                                className="form-control"
                                                                value={formData.joining_date || ''}
                                                                onChange={handleInputChange}
                                                            />
                                                        ) : (
                                                            <p className="text-dark mb-0">{formatDate(user?.joining_date)}</p>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Role */}
                                                <div className="col-md-6 mb-3">
                                                    <div className="info-item">
                                                        <label className="text-muted mb-1">Role</label>
                                                        <p className="text-dark mb-0">
                                                            <span className="badge bg-primary bg-opacity-10 text-primary">
                                                                {user?.role_name || 'N/A'}
                                                            </span>
                                                        </p>
                                                    </div>
                                                </div>

                                                {/* Address - Full Width */}
                                                <div className="col-12 mb-3">
                                                    <div className="info-item">
                                                        <label className="text-muted mb-1">Address</label>
                                                        {editing ? (
                                                            <textarea
                                                                name="address"
                                                                className="form-control"
                                                                rows="2"
                                                                value={formData.address || ''}
                                                                onChange={handleInputChange}
                                                                placeholder="Enter your address"
                                                            ></textarea>
                                                        ) : (
                                                            <p className="text-dark mb-0">{user?.address || 'N/A'}</p>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* City, State, Pincode */}
                                                <div className="col-md-4 mb-3">
                                                    <div className="info-item">
                                                        <label className="text-muted mb-1">City</label>
                                                        {editing ? (
                                                            <input
                                                                type="text"
                                                                name="city"
                                                                className="form-control"
                                                                value={formData.city || ''}
                                                                onChange={handleInputChange}
                                                                placeholder="Enter city"
                                                            />
                                                        ) : (
                                                            <p className="text-dark mb-0">{user?.city || 'N/A'}</p>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="col-md-4 mb-3">
                                                    <div className="info-item">
                                                        <label className="text-muted mb-1">State</label>
                                                        {editing ? (
                                                            <input
                                                                type="text"
                                                                name="state"
                                                                className="form-control"
                                                                value={formData.state || ''}
                                                                onChange={handleInputChange}
                                                                placeholder="Enter state"
                                                            />
                                                        ) : (
                                                            <p className="text-dark mb-0">{user?.state || 'N/A'}</p>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="col-md-4 mb-3">
                                                    <div className="info-item">
                                                        <label className="text-muted mb-1">Pincode</label>
                                                        {editing ? (
                                                            <input
                                                                type="text"
                                                                name="pincode"
                                                                className="form-control"
                                                                value={formData.pincode || ''}
                                                                onChange={handleInputChange}
                                                                placeholder="Enter pincode"
                                                            />
                                                        ) : (
                                                            <p className="text-dark mb-0">{user?.pincode || 'N/A'}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Change Password Card */}
                        <div className="card mt-4">
                            <div className="card-header">
                                <div className="row align-items-center">
                                    <div className="col">
                                        <h5 className="card-title text-dark mb-0">
                                            <Lock size={18} className="me-2" />
                                            Change Password
                                        </h5>
                                    </div>
                                    <div className="col-auto">
                                        <button 
                                            type="button" 
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={resetPasswordForm}
                                        >
                                            Clear
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    {/* Current Password */}
                                    <div className="col-md-4 mb-3">
                                        <label className="text-muted mb-1">Current Password</label>
                                        <div className="input-group">
                                            <input 
                                                type={showCurrentPassword ? 'text' : 'password'}
                                                name="current_password"
                                                className="form-control" 
                                                placeholder="Enter current password"
                                                value={passwordData.current_password}
                                                onChange={handlePasswordChange}
                                                disabled={changingPassword}
                                            />
                                            <button 
                                                className="btn btn-outline-secondary" 
                                                type="button"
                                                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                            >
                                                {showCurrentPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                            </button>
                                        </div>
                                    </div>

                                    {/* New Password */}
                                    <div className="col-md-4 mb-3">
                                        <label className="text-muted mb-1">New Password</label>
                                        <div className="input-group">
                                            <input 
                                                type={showNewPassword ? 'text' : 'password'}
                                                name="new_password"
                                                className="form-control" 
                                                placeholder="Enter new password"
                                                value={passwordData.new_password}
                                                onChange={handlePasswordChange}
                                                disabled={changingPassword}
                                            />
                                            <button 
                                                className="btn btn-outline-secondary" 
                                                type="button"
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                            >
                                                {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                            </button>
                                        </div>
                                        <small className="text-muted">Minimum 6 characters</small>
                                    </div>

                                    {/* Confirm New Password */}
                                    <div className="col-md-4 mb-3">
                                        <label className="text-muted mb-1">Confirm New Password</label>
                                        <div className="input-group">
                                            <input 
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                name="confirm_password"
                                                className="form-control" 
                                                placeholder="Confirm new password"
                                                value={passwordData.confirm_password}
                                                onChange={handlePasswordChange}
                                                disabled={changingPassword}
                                            />
                                            <button 
                                                className="btn btn-outline-secondary" 
                                                type="button"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            >
                                                {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Password Strength Indicator */}
                                    {passwordData.new_password && (
                                        <div className="col-12 mb-3">
                                            <div className="password-strength">
                                                <small className="text-muted me-2">Password Strength:</small>
                                                {passwordData.new_password.length < 6 ? (
                                                    <span className="badge bg-danger">Too Short</span>
                                                ) : passwordData.new_password.length < 8 ? (
                                                    <span className="badge bg-warning">Weak</span>
                                                ) : passwordData.new_password.length < 10 ? (
                                                    <span className="badge bg-info">Medium</span>
                                                ) : (
                                                    <span className="badge bg-success">Strong</span>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Action Buttons */}
                                    <div className="col-12 mt-2">
                                        <button 
                                            type="button" 
                                            className="btn btn-primary"
                                            onClick={handlePasswordChangeSubmit}
                                            disabled={changingPassword}
                                        >
                                            {changingPassword ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                                    Updating...
                                                </>
                                            ) : (
                                                'Update Password'
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Profile;